import React, {useState, useEffect} from "react";

// Redux
import {useSelector, useDispatch} from "react-redux";
import {
  fetchEventSource,
  editEventSource,
  deleteEventSource,
} from "../../../redux/features/events/sourcesSlice";

// Routing
import {useParams, useHistory, Redirect} from "react-router-dom";

// Components
import Typography from "@material-ui/core/Typography";
import Grid from "@material-ui/core/Grid";
import Paper from "../../UI/MainPaper";
import EventSourcesForm from "./EventSourcesForm";
import FullContainerProgress from "../../UI/FullContainerProgress";
import DeleteButton from "../../Buttons/DeleteButton";
import DeleteDialog from "../../UI/DeleteDialog";
import BreadcrumbsWithEdit from "../../Navigation/BreadcrumbsWithEdit";

const EventSourcesEdit = () => {
  // Local State
  const [isEditMode, setEditMode] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Routing
  const {id} = useParams();
  const history = useHistory();

  // Redux
  const dispatch = useDispatch();
  const {currentPageEventSource, isLoading, error} = useSelector(
    state => state.eventSources
  );
  const {userPermission} = useSelector(state => {
    return state.auth;
  });
  const {write, delete: deletePermission} = userPermission.events;

  const formattedValues = {
    name: currentPageEventSource.name,
    src: currentPageEventSource.src,
    frequency: currentPageEventSource.frequency,
    event_status: currentPageEventSource.event_status,
    import_type: currentPageEventSource.import_type,
  };

  let breadcrumbNameMap = {
    "/events": "Events",
    "/events/sources": "Event Sources",
  };
  breadcrumbNameMap[`/events/sources/${id}`] = currentPageEventSource.src;

  useEffect(() => {
    // check if the id is a int to keep going
    // This if is why javascript is broken :)
    if (Number.isInteger(id * 1) && id * 1 < Number.MAX_SAFE_INTEGER) {
      dispatch(fetchEventSource(id));
    } else {
      history.push("/404");
    }
  }, [dispatch, history, id]);

  useEffect(() => {
    setEditMode(write);
  }, [write]);

  const onSubmit = async values => {
    const eventSourceObj = {
      events_sources_id: id,
      ...values,
    };
    // Logic to show error or success
    const res = await dispatch(editEventSource(eventSourceObj));
    if (!res.error) {
      history.push({
        pathname: "/events/sources",
        state: {
          showSuccess: true,
          snackbarContent: `Your changes to ${eventSourceObj.name} were saved successfully!`,
          snackbarLink: `/events/sources/${eventSourceObj.source_id}`,
        },
      });
    }
  };

  const onDelete = async () => {
    // Logic to show error or success
    const res = await dispatch(deleteEventSource(id));
    if (!res.error) {
      history.push({
        pathname: "/events/sources",
        state: {
          showSuccess: true,
          snackbarContent: `The Event Source was successfully deleted!`,
        },
      });
    }
  };

  // Redirect if source doesn't exist'
  if (error && error.errorCode === 404) {
    return <Redirect to={"/404"} />;
  }

  return (
    <>
      <Grid container spacing={2}>
        <BreadcrumbsWithEdit
          isLoading={isLoading}
          breadcrumbNameMap={breadcrumbNameMap}
          isEditMode={isEditMode}
          setEditMode={setEditMode}
          writePermission={write}
        />
        <Grid item xs={12}>
          <Paper>
            {isLoading ? (
              <FullContainerProgress />
            ) : (
              <>
                <Typography component="h1" variant="h5" gutterBottom>
                  {currentPageEventSource.name}
                </Typography>
                <EventSourcesForm
                  defaultValues={formattedValues}
                  readOnly={!isEditMode}
                  submitButtonLabel="Save Changes"
                  onSubmit={onSubmit}
                  onCancel={() => setEditMode(false)}
                />
              </>
            )}
          </Paper>
        </Grid>
        {isEditMode && !isLoading && !!deletePermission && (
          <Grid item xs={12}>
            <DeleteButton onClick={() => setDialogOpen(true)} size="large">
              Delete Event Source
            </DeleteButton>
          </Grid>
        )}
      </Grid>
      {!!deletePermission && (
        <DeleteDialog
          dialogTitle="Are you sure you want to delete this Event Source?"
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSubmit={() => onDelete()}
        />
      )}
    </>
  );
};

export default EventSourcesEdit;
